import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import type { FileUploadItem } from "../types";
import type { ProgressMessage } from "../state";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";

interface ProcessingStepProps {
  files: FileUploadItem[];
  currentFileName?: string | null;
  progressMessages: ProgressMessage[];
  onCancel?: () => void;
}

export function ProcessingStep({
  files,
  currentFileName,
  progressMessages,
  onCancel,
}: ProcessingStepProps) {
  const inFlight = files.filter((f) => f.status === "Processing");
  const done = files.filter((f) => f.status === "Completed");
  const failed = files.filter((f) => f.status === "Failed");

  const logRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [progressMessages.length]);

  return (
    <div className="animate-reveal">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="max-w-2xl">
          <div className="label mb-2">In progress</div>
          <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
            Processing your files
          </h1>
          <p className="mt-4 text-[1.05rem] leading-relaxed text-ink-soft">
            {currentFileName ? (
              <>
                Working on{" "}
                <span className="font-mono text-[0.95rem] text-primary">
                  {currentFileName}
                </span>
                …
              </>
            ) : (
              "Extracting and analyzing document content…"
            )}
          </p>
        </div>
        {onCancel && (
          <Button intent="secondary" size="md" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>

      <div className="mt-8 max-w-3xl">
        <Bar />
      </div>

      <div className="mt-8 grid max-w-5xl grid-cols-1 gap-6 lg:grid-cols-5">
        {/* Files panel */}
        <div className="lg:col-span-2 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
          <div className="border-b border-rule px-5 py-3">
            <div className="text-[0.95rem] font-semibold text-ink">Files</div>
          </div>
          <ul className="divide-y divide-rule">
            {files.map((f) => (
              <li
                key={f.id}
                className="flex items-center gap-3 px-5 py-3"
              >
                <StatusDot status={f.status} />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-[0.92rem] text-ink">
                    {f.file.name}
                  </div>
                  {f.status === "Failed" && f.errorMessage && (
                    <div className="mt-0.5 text-[0.78rem] text-danger">
                      {f.errorMessage}
                    </div>
                  )}
                  {f.status === "Completed" && f.parsed && (
                    <div className="mt-0.5 text-[0.78rem] text-sage-ink">
                      {f.parsed.objections.length} objection
                      {f.parsed.objections.length === 1 ? "" : "s"}
                    </div>
                  )}
                </div>
                <StatusBadge status={f.status} />
              </li>
            ))}
          </ul>
        </div>

        {/* Live log */}
        <div className="lg:col-span-3 overflow-hidden rounded-lg border border-rule bg-surface shadow-soft">
          <div className="flex items-baseline justify-between border-b border-rule px-5 py-3">
            <div className="text-[0.95rem] font-semibold text-ink">
              Activity
            </div>
            <div className="text-[0.78rem] text-ink-muted">
              {progressMessages.length} event
              {progressMessages.length === 1 ? "" : "s"}
            </div>
          </div>
          <div
            ref={logRef}
            className="h-[300px] overflow-y-auto bg-canvas-2/60 px-5 py-3"
          >
            {progressMessages.length === 0 ? (
              <div className="text-[0.88rem] text-ink-muted">
                Waiting for the first event…
              </div>
            ) : (
              <ul className="space-y-1 font-mono text-[0.82rem] leading-relaxed">
                {progressMessages.map((m, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="shrink-0 text-ink-faint">
                      {m.timestamp}
                    </span>
                    {m.fileName && (
                      <span className="shrink-0 truncate max-w-[10rem] text-primary-ink">
                        {m.fileName}
                      </span>
                    )}
                    <span className="break-words text-ink">{m.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="mt-5 flex gap-4 text-[0.88rem]">
        <Pill tint="sage">{done.length} done</Pill>
        <Pill tint="primary">{inFlight.length} in flight</Pill>
        {failed.length > 0 && <Pill tint="danger">{failed.length} failed</Pill>}
      </div>
    </div>
  );
}

function Bar() {
  return (
    <div className="h-1.5 overflow-hidden rounded-full bg-rule">
      <motion.div
        animate={{ x: ["-100%", "200%"] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
        className="h-full w-1/3 rounded-full bg-primary"
      />
    </div>
  );
}

function StatusDot({ status }: { status: FileUploadItem["status"] }) {
  const map = {
    Pending: "bg-rule",
    Processing: "bg-primary animate-pulse",
    Completed: "bg-sage",
    Failed: "bg-danger",
  } as const;
  return (
    <span
      aria-hidden
      className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full ${map[status]}`}
    />
  );
}

function StatusBadge({ status }: { status: FileUploadItem["status"] }) {
  const map = {
    Pending: { txt: "Queued", cls: "bg-canvas-2 text-ink-muted" },
    Processing: { txt: "Working…", cls: "bg-primary-soft text-primary-ink" },
    Completed: { txt: "Done", cls: "bg-sage-soft text-sage-ink" },
    Failed: { txt: "Failed", cls: "bg-danger-soft text-danger" },
  } as const;
  const { txt, cls } = map[status];
  return (
    <span
      className={cn("rounded-full px-2.5 py-0.5 text-[0.74rem] font-medium", cls)}
    >
      {txt}
    </span>
  );
}

function Pill({
  children,
  tint,
}: {
  children: React.ReactNode;
  tint: "sage" | "primary" | "danger";
}) {
  const map = {
    sage: "bg-sage-soft text-sage-ink",
    primary: "bg-primary-soft text-primary-ink",
    danger: "bg-danger-soft text-danger",
  }[tint];
  return (
    <span className={`inline-flex items-center rounded-full px-3 py-1 font-medium ${map}`}>
      {children}
    </span>
  );
}
